import { QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { useAntdTable, useMemoizedFn, useRequest } from 'ahooks';
import { Button, Col, message, Row, Select, Space, Table } from 'antd';
import QuickRangePicker, { QuickAction } from 'components/QuickRangePicker';
import dayjs, { Dayjs } from 'lib/dayjs';
import { useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { getWabaAccountList, getWhatsappCostList } from 'services/api/whatsapp';
import { numDisplay } from '../helpers';
import { openExternalURL } from 'utils/openExternalURL';

export interface WACostsProps {}

const CHARGING_RULES_DOC_LINK =
  'https://leyan.yuque.com/docs/share/4f535303-4545-4612-99ab-b41399513cc8?#';

const WACosts = (props: WACostsProps) => {
  const { t } = useTranslate();
  const [accountFilter, setAccountFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<Array<Dayjs>>([]);
  const { data: accountRes } = useRequest(getWabaAccountList, {
    onError() {
      message.error(t('WhatsApp.获取whatsapp帐号列表失败'));
    },
  });
  const accountList = accountRes?.accounts;
  const { tableProps, data: costData } = useAntdTable(
    ({ current, pageSize }) =>
      getWhatsappCostList({
        pageNum: current,
        pageSize,
        wabaId: accountFilter,
        startTime: dateRange[0] ? dateRange[0].valueOf() : undefined,
        endTime: dateRange[1] ? dateRange[1].valueOf() : undefined,
      }).then((res) => {
        return {
          total: res.totalRows,
          list: res.details,
          totalCost: res.totalCost,
        };
      }),
    {
      refreshDeps: [accountFilter, dateRange],
      onError() {
        message.error(t('WhatsApp.获取Whatsapp消费记录失败'));
      },
    },
  );
  const quickActions: QuickAction[] = useMemo(
    () => [
      {
        key: 'last7days',
        label: t('ViewDashboard.last7days'),
        range: [dayjs().subtract(6, 'day'), dayjs()],
      },
      {
        key: 'last15days',
        label: t('ViewDashboard.last30days'),
        range: [dayjs().subtract(29, 'day'), dayjs()],
      },
    ],
    [t],
  );
  const columns = useMemo(() => {
    return [
      {
        dataIndex: 'id',
        title: 'ID',
      },
      {
        dataIndex: 'phone',
        title: t('WhatsApp.table.account'),
      },
      {
        dataIndex: 'cost',
        title: t('WhatsApp.table.cost'),
      },
      {
        dataIndex: 'createAt',
        title: t('WhatsApp.table.createAt'),
        render(value: string) {
          return dayjs(Number(value)).format('YYYY-MM-DD HH:mm:ss');
        },
      },
      {
        dataIndex: 'conversationType',
        title: t('WhatsApp.table.conversationType.label'),
        render(value: WhatsappBusinessApi.CostDetail['conversationType']) {
          return t(`table.conversationType.${value}`);
        },
      },
      {
        dataIndex: 'userPhone',
        title: t('WhatsApp.table.buyerMobile'),
      },
    ];
  }, [t]);
  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Select
            value={accountFilter}
            onChange={setAccountFilter}
            style={{
              width: '260px',
            }}
            placeholder={t('WhatsApp.请选择Whatsapp帐号')}
            allowClear
            options={accountList}
            fieldNames={{ label: 'phone', value: 'wabaId' }}
          />
        </Col>
        <Col>
          <Button
            type="link"
            onClick={useMemoizedFn(() => openExternalURL(CHARGING_RULES_DOC_LINK, '_blank'))}
          >
            <Space>
              {t('WhatsApp.计费规则说明')}
              <QuestionCircleOutlined />
            </Space>
          </Button>
          {/* </Tooltip> */}
        </Col>
      </Row>
      <Row
        className={css`
          margin: 12px 0;
        `}
        justify="space-between"
        align="middle"
      >
        <Col>
          <div>{t('WhatsApp.总消费x', { x: numDisplay(costData?.totalCost) })}</div>
        </Col>
        <Col>
          <QuickRangePicker
            quickActions={quickActions}
            value={dateRange.map((x) => dayjs(x)) as any}
            onChange={(value) => {
              setDateRange((value as any) || []);
            }}
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        {...tableProps}
        pagination={{ ...tableProps.pagination, showTotal: (x) => t('WhatsApp.共x项', { x }) }}
      />
    </>
  );
};
export default WACosts;
